import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import { sizes } from '../utils/breakpoints'
import { useMediaQuery } from 'react-responsive'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '../context/ThemeModeContext'
import heroLight from '../assets/images/hero-wallpaper-light.jpg'
import heroLightMobile from '../assets/images/hero-wallpaper-light-mobile.jpg'
import heroDark from '../assets/images/hero-wallpaper-dark.jpg'
import heroDarkMobile from '../assets/images/hero-wallpaper-dark-mobile.jpg'

const BannerComponent = () => {
  const isMobile = useMediaQuery({ maxWidth: sizes.mobile })
  const { t } = useTranslation()
  const { mode } = useThemeMode()

  const desktopImage = mode === 'dark' ? heroDark : heroLight
  const mobileImage = mode === 'dark' ? heroDarkMobile : heroLightMobile

  return (
    <Container $desktopImage={desktopImage} $mobileImage={mobileImage}>
      <TextContainer>
        <FirstLineTitle>{t('banner.line1')}</FirstLineTitle>
        <SecondLineTitle>{t('banner.line2')}</SecondLineTitle>
        {!isMobile && (
          <SubtitleContainer>
            <FirstLineSubtitle>{t('banner.subtitle')}</FirstLineSubtitle>
          </SubtitleContainer>
        )}
      </TextContainer>
    </Container>
  )
}

export default BannerComponent

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(28px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const animatedLine = css`
  opacity: 0;
  animation: ${fadeInUp} 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`

const Container = styled.div`
  height: calc(100vh - 70px);
  background-image: url(${({ $desktopImage }) => $desktopImage});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  transition: background-image 0.2s ease;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin-top: 70px;
  padding-left: 150px;

  @media (max-width: ${sizes.mobile}px) {
    height: 640px;
    background-image: url(${({ $mobileImage }) => $mobileImage});
    margin-top: 50px;
    padding-left: 20px;
  }
`

const TextContainer = styled.div`
  color: ${({ theme }) => theme.colors.text.hero};
  display: flex;
  flex-direction: column;
  max-width: 600px;
  margin-top: 96px;

  @media (max-width: ${sizes.mobile}px) {
    margin-top: 120px;
    max-width: 275px;
  }
`

const FirstLineTitle = styled.p`
  ${animatedLine}
  animation-delay: 0.1s;
  line-height: 38.4px;
  font-size: 32px;
  color: ${({ theme }) => theme.colors.text.hero};
  font-family: ${({ theme }) => theme.fonts.body};
  margin: 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 20px;
    line-height: 24px;
  }
`

const SecondLineTitle = styled.p`
  ${animatedLine}
  animation-delay: 0.25s;
  color: ${({ theme }) => theme.colors.text.hero};
  line-height: 76.8px;
  font-size: 64px;
  font-family: ${({ theme }) => theme.fonts.headingAlt};
  margin-top: 25px;
  margin-bottom: 0px;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 32px;
    line-height: 38.4px;
    margin: 14px 0px;
  }
`

const SubtitleContainer = styled.div`
  ${animatedLine}
  animation-delay: 0.45s;
  max-width: 577px;
`

const FirstLineSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.hero};
  line-height: 24px;
  font-size: 20px;
  font-family: ${({ theme }) => theme.fonts.body};
  margin-top: 20px;
`
